// ABOUTME: Main tour component that renders the Joyride walkthrough.
// ABOUTME: Handles step navigation, styling, and interactive callbacks.

import { useCallback, useMemo, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTour } from '@/context/TourContext';
import { tourSteps, TOTAL_STEPS } from './tourSteps';

// Obsidian theme colors for Joyride
const joyrideStyles = {
  options: {
    backgroundColor: 'rgba(30, 31, 38, 0.98)',
    arrowColor: 'rgba(30, 31, 38, 0.98)',
    overlayColor: 'rgba(0, 0, 0, 0.6)',
    primaryColor: '#818cf8', // indigo accent
    textColor: '#f1f5f9',
    spotlightShadow: '0 0 15px rgba(129, 140, 248, 0.5)',
    zIndex: 10000,
  },
  tooltip: {
    borderRadius: 16,
    padding: 24,
    maxWidth: 'min(400px, calc(100vw - 48px))',
  },
  tooltipContainer: {
    textAlign: 'left' as const,
  },
  tooltipTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: '#f1f5f9',
    marginBottom: 8,
  },
  tooltipContent: {
    fontSize: 14,
    color: '#cbd5e1',
    lineHeight: 1.6,
  },
  buttonNext: {
    backgroundColor: '#818cf8',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 500,
    padding: '10px 20px',
    color: '#ffffff',
  },
  buttonBack: {
    color: '#94a3b8',
    fontSize: 14,
    marginRight: 8,
  },
  buttonSkip: {
    color: '#64748b',
    fontSize: 13,
  },
  buttonClose: {
    color: '#94a3b8',
  },
  spotlight: {
    borderRadius: 12,
  },
};

export function AppTour() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isRunning,
    stepIndex,
    nextStep,
    prevStep,
    goToStep,
    skipTour,
    completeTour,
    isWaitingForAction,
  } = useTour();

  // Get current step config
  const currentStepConfig = tourSteps[stepIndex];

  // Add data attribute to body when on click-through steps
  // This allows CSS to make the overlay non-blocking for these steps
  useEffect(() => {
    if (isRunning && currentStepConfig?.spotlightClicks) {
      document.body.setAttribute('data-tour-click-through', 'true');
    } else {
      document.body.removeAttribute('data-tour-click-through');
    }

    return () => {
      document.body.removeAttribute('data-tour-click-through');
    };
  }, [isRunning, currentStepConfig]);

  // Handle automatic navigation when step changes require a different page
  // This handles programmatic step changes from TourContext (e.g., after interactive actions)
  useEffect(() => {
    if (!isRunning || !currentStepConfig?.prerequisitePage) {
      return;
    }

    const requiredPath = currentStepConfig.prerequisitePage;
    const currentPath = location.pathname;

    // Navigate if not on the required page
    if (!currentPath.startsWith(requiredPath)) {
      console.log('[AppTour] Navigating to prerequisite page:', requiredPath);
      navigate(requiredPath);
    }
  }, [isRunning, stepIndex, currentStepConfig, location.pathname, navigate]);

  // Handle step changes and navigation requirements
  const handleJoyrideCallback = useCallback(
    async (data: CallBackProps) => {
      const { action, index, status, type } = data;

      console.log('[AppTour] Joyride callback:', { action, index, status, type });

      // Handle tour completion
      if (status === STATUS.FINISHED) {
        await completeTour();
        return;
      }

      // Handle tour skip
      if (status === STATUS.SKIPPED) {
        await skipTour();
        return;
      }

      // Handle step changes (only if not waiting for interactive action)
      if (type === EVENTS.STEP_AFTER && !isWaitingForAction) {
        if (action === ACTIONS.NEXT) {
          const nextStepIndex = index + 1;
          const nextStepConfig = tourSteps[nextStepIndex];

          // Check if next step requires navigation
          if (nextStepConfig?.prerequisitePage) {
            const currentPath = location.pathname;
            const requiredPath = nextStepConfig.prerequisitePage;

            // Navigate if not on the required page
            if (!currentPath.startsWith(requiredPath)) {
              navigate(requiredPath);
              // Small delay to let navigation complete
              setTimeout(() => {
                goToStep(nextStepIndex);
              }, 300);
              return;
            }
          }

          nextStep();
        } else if (action === ACTIONS.PREV) {
          prevStep();
        }
      }

      // Handle close button
      if (action === ACTIONS.CLOSE) {
        await skipTour();
      }
    },
    [navigate, location.pathname, nextStep, prevStep, goToStep, skipTour, completeTour, isWaitingForAction]
  );

  // Custom locale for button text - hide next button for interactive steps
  const locale = useMemo(
    () => ({
      back: 'Back',
      close: 'Close',
      last: 'Finish',
      next: currentStepConfig?.hideNextButton ? '' : (stepIndex === TOTAL_STEPS - 1 ? 'Finish' : 'Next'),
      open: 'Open',
      skip: 'Skip Tour',
    }),
    [stepIndex, currentStepConfig]
  );

  // Dynamically update styles based on current step
  const dynamicStyles = useMemo(() => {
    const styles = { ...joyrideStyles };

    // Hide next button for interactive steps
    if (currentStepConfig?.hideNextButton) {
      styles.buttonNext = {
        ...styles.buttonNext,
        display: 'none',
      } as typeof styles.buttonNext;
    }

    // Hide back button for ephemeral steps (after click-send)
    if (currentStepConfig?.hideBackButton) {
      styles.buttonBack = {
        ...styles.buttonBack,
        display: 'none',
      } as typeof styles.buttonBack;
    }

    return styles;
  }, [currentStepConfig]);

  // Don't show tour on onboarding page - wait until user goes to home page
  if (!isRunning || location.pathname === '/onboarding') {
    return null;
  }

  return (
    <Joyride
      steps={tourSteps}
      stepIndex={stepIndex}
      run={isRunning}
      continuous
      showProgress
      showSkipButton
      hideCloseButton={currentStepConfig?.hideCloseButton || false}
      disableScrolling={false}
      disableOverlayClose
      disableOverlay={false}
      spotlightClicks={currentStepConfig?.spotlightClicks || false}
      spotlightPadding={8}
      callback={handleJoyrideCallback}
      locale={locale}
      styles={dynamicStyles}
      floaterProps={{
        modifiers: {
          preventOverflow: {
            options: {
              rootBoundary: 'viewport',
              padding: 16,
            },
          },
        },
      }}
    />
  );
}
